import type { ComputedRef } from 'vue'
import { computed } from 'vue'
import { useBloxRuntime, usePageContext } from '@bagelink/blox'
import { isPublicRecord, publicationScope } from '../utils/publication'

/**
 * Entity resolved by a CMS template's `$page` datastore binding.
 *
 * Existing views keep their route/datastore fallback so they still work while
 * explicit Vue routes are active. In PageDesigner and CmsPageView, `$page`
 * supplies the selected/resolved record without relying on route params.
 */
export function useBoundPageItem<T extends Record<string, unknown> = Record<string, any>>(): ComputedRef<T | null> {
	const page = usePageContext<T>('$page')
	const { isPreview } = useBloxRuntime()
	return computed(() => {
		const row = page.value
		if (!row || isPreview.value) return row
		return Object.keys(publicationScope.collections).some(collection => isPublicRecord(collection, row)) ? row : null
	})
}

/**
 * The Blox preview is a dedicated iframe route. Public CMS pages never use it,
 * so local fixture records can safely be restricted to this mode.
 */
export function useBloxPreviewMode(): ComputedRef<boolean> {
	const { isPreview } = useBloxRuntime()
	return computed(() => isPreview.value)
}

/**
 * Normalize bound and preview fixture records through the same code path.
 * A fixture is never selected on a public route.
 */
export function useBoundPageModel<
	TRecord extends Record<string, unknown>,
	TModel,
>(
	normalize: (record: TRecord) => TModel,
	previewRecord: TRecord,
): ComputedRef<TModel | null> {
	const bound = useBoundPageItem<TRecord>()
	const { isPreview, loading } = useBloxRuntime()

	return computed(() => {
		if (bound.value) return normalize(bound.value)
		if (isPreview.value && !loading.value) return normalize(previewRecord)
		return null
	})
}
